import React, { useState, useEffect, useMemo } from 'react'; // Ajout de useMemo
import { useParams, Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Table,
  Modal,
  Tabs,
  Tab,
  Container, // Ajouté pour la structure globale
  Spinner, // Ajouté pour le chargement
  Nav
} from 'react-bootstrap';
import * as Moment from 'moment';
import NavbarStandard from '@commons/NavbarStandard';
import Section from '@common/Section';
import FooterStandard from '@commons/FooterStandard';
import api from '@services/api'; // Assurez-vous que ce chemin est correct
import { toast } from 'react-toastify';
import bgPartners from '@assets/img/tredunion/bg_fiche.jpeg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Pour les icônes
import {
  faUsers,
  faPlus,
  faTrash,
  faListAlt,
  faBuilding,
  faHandshake,
  faSearch,
  faArrowLeft,
  faCalendar,
  faClock,
  faBusinessTime,
  faUserClock,
  faChartPie,
  faUserPlus,
  faUserMinus,
  faUser
} from '@fortawesome/free-solid-svg-icons';

const SalonDetail = () => {
  const { id: salonId } = useParams(); // Renommé en salonId pour clarté
  const [loading, setLoading] = useState(true);
  const [salon, setSalon] = useState(null);
  const [participants, setParticipants] = useState([]); // Participants du partenaire
  const [allCompanies, setAllCompanies] = useState([]); // Toutes les sociétés du salon, avec `is_partner_company`
  const [existingWishes, setExistingWishes] = useState([]); // Vœux des participants du partenaire
  const [searchCompany, setSearchCompany] = useState('');
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false); // Renommé pour clarté
  const [showOnlyWishedToggle, setShowOnlyWishedToggle] = useState(false); // Modifié: initialisé à false
  const [activeTab, setActiveTab] = useState('summary');
  const [participantForm, setParticipantForm] = useState({
    firstname: '',
    lastname: ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // Pour la confirmation de suppression
  const [participantToDelete, setParticipantToDelete] = useState(null); // Pour la suppression

  useEffect(() => {
    fetchSalonDetails();
  }, [salonId]);

  const fetchSalonDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/partner/partner-salons/${salonId}/details`
      );

      if (response.data.success) {
        const {
          salon: salonData,
          participants: participantsData,
          all_companies: allCompaniesDataFromApi,
          existing_wishes: existingWishesData
        } = response.data.data;

        setSalon(salonData);
        setParticipants(participantsData || []);
        setAllCompanies(allCompaniesDataFromApi || []);
        setExistingWishes(existingWishesData || []);

        if (participantsData && participantsData.length > 0) {
          setActiveTab('summary');
        } else {
          setActiveTab('add');
        }
      } else {
        toast(
          response.data.data?.error ||
            'Impossible de charger les détails du salon'
        );
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails du salon:', error);
      toast('Une erreur est survenue lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleParticipantFormChange = e => {
    setParticipantForm({
      ...participantForm,
      [e.target.name]: e.target.value
    });
  };

  const addParticipant = async () => {
    if (!participantForm.firstname.trim() || !participantForm.lastname.trim()) {
      toast.warn('Veuillez remplir le prénom et le nom du participant.');
      return;
    }
    try {
      const response = await api.post(
        `/partner/partner-salons/${salonId}/participants`,
        participantForm
      );

      if (response.data.success) {
        let newParticipant = response.data.data;
        newParticipant.user_id = null;

        setParticipants(prev => [...prev, newParticipant]);
        setParticipantForm({
          firstname: '',
          lastname: '',
          email: '',
          phone: ''
        });
        setShowAddParticipantModal(false);
        toast('Participant ajouté avec succès');
        setActiveTab(newParticipant.id.toString());
      } else {
        toast(response.data.message || "Erreur lors de l'ajout du participant");
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout du participant:", error);
      toast(error.response?.data?.message || 'Une erreur est survenue');
    }
  };

  const confirmDeleteParticipant = participant => {
    setParticipantToDelete(participant);
    setShowDeleteConfirm(true);
  };

  const deleteParticipant = async () => {
    if (!participantToDelete) return;
    try {
      await api.delete(
        `/partner/partner-salons/${salonId}/participants/${participantToDelete.id}`
      );
      setParticipants(
        participants.filter(p => p.id !== participantToDelete.id)
      );
      setExistingWishes(
        existingWishes.filter(
          wish => wish.partner_participant_id !== participantToDelete.id
        )
      );
      toast('Participant supprimé avec succès');
      if (activeTab === participantToDelete.id.toString()) {
        setActiveTab('summary');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du participant:', error);
      toast('Une erreur est survenue lors de la suppression');
    } finally {
      setShowDeleteConfirm(false);
      setParticipantToDelete(null);
    }
  };

  const toggleParticipantWish = async (
    participantId,
    companyId,
    currentIsWished
  ) => {
    const newIsWished = !currentIsWished;
    const maxWishes = salon?.max_wishes_per_participant;

    if (newIsWished) {
      const currentWishesForParticipant = existingWishes.filter(
        wish => wish.partner_participant_id === participantId
      ).length;

      // Vérifier si maxWishes est défini et est un nombre
      if (typeof maxWishes === 'number') {
        if (maxWishes > 0 && currentWishesForParticipant >= maxWishes) {
          toast.warn(
            `Limite de ${maxWishes} vœux atteinte pour ce participant.`
          );
          return;
        }
      } else {
        console.warn(
          "max_wishes_per_participant n'est pas défini dans l'objet salon."
        );
      }
    }

    try {
      const response = await api.post(
        `/partner/partner-salons/${salonId}/wishes`,
        {
          partner_participant_id: participantId,
          company_id: companyId,
          is_wished: newIsWished
        }
      );

      if (response.data.success) {
        if (newIsWished) {
          setExistingWishes(prevWishes => [
            ...prevWishes.filter(
              wish =>
                !(
                  wish.partner_participant_id === participantId &&
                  wish.company_id === companyId
                )
            ),
            {
              partner_participant_id: participantId,
              company_id: companyId
            }
          ]);
        } else {
          setExistingWishes(prevWishes =>
            prevWishes.filter(
              wish =>
                !(
                  wish.partner_participant_id === participantId &&
                  wish.company_id === companyId
                )
            )
          );
        }
        toast(`Vœu ${newIsWished ? 'ajouté' : 'retiré'} avec succès`);
      } else {
        toast(
          response.data.message || 'Erreur lors de la mise à jour du souhait'
        );
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du souhait:', error);
      toast('Une erreur est survenue lors de la mise à jour du souhait');
    }
  };

  const isCompanyWishedByParticipant = (participantId, companyId) => {
    return existingWishes.some(
      wish =>
        wish.partner_participant_id === participantId &&
        wish.company_id === companyId
    );
  };

  const getWishSummary = () => {
    return participants.map(participant => {
      const wishedCompaniesList = allCompanies.filter(company =>
        isCompanyWishedByParticipant(participant.id, company.id)
      );
      return {
        participant,
        count: wishedCompaniesList.length,
        wishedCompanies: wishedCompaniesList
      };
    });
  };

  const handleTabChange = tabKey => {
    setActiveTab(tabKey);
    if (tabKey === 'add') {
      setShowAddParticipantModal(true);
    } else {
      setShowAddParticipantModal(false);
    }
  };

  const isRegistrationOpen = salon?.registration_end_date
    ? new Date(salon.registration_end_date) >= new Date()
    : true;

  const sortByPartnerCompanyAndName = (a, b) => {
    if (a.is_partner_company && !b.is_partner_company) return -1;
    if (!a.is_partner_company && b.is_partner_company) return 1;
    return a.name.localeCompare(b.name);
  };

  const filteredCompaniesForDisplay = useMemo(() => {
    if (!searchCompany) {
      return allCompanies;
    }
    return allCompanies.filter(company =>
      company.name.toLowerCase().includes(searchCompany.toLowerCase())
    );
  }, [allCompanies, searchCompany]);

  if (loading) {
    return (
      <>
        <NavbarStandard />
        <Section
          bg="light"
          className="d-flex align-items-end p-1 mt-7"
          id="actualites"
          style={{ backgroundImage: `url("${bgPartners}")` }}
        >
          <Row>
            <Col xs={12}>
              <h2>Espace partenaire</h2>
            </Col>
          </Row>
        </Section>
        <Section
          className="py-0 overflow-hidden light"
          position="center bottom"
        >
          <Container className="text-center mt-5 py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </Spinner>
          </Container>
        </Section>
        <FooterStandard />
      </>
    );
  }

  if (!salon) {
    return (
      <>
        <NavbarStandard />
        <Section
          bg="light"
          className="d-flex align-items-end p-1 mt-7"
          id="actualites"
          style={{ backgroundImage: `url("${bgPartners}")` }}
        >
          <Row>
            <Col xs={12}>
              <h2>Espace partenaire</h2>
            </Col>
          </Row>
        </Section>
        <Section
          className="py-0 overflow-hidden light"
          position="center bottom"
        >
          <Container className="text-center mt-5 py-5">
            <Alert variant="danger">
              Salon non trouvé ou vous n'êtes pas autorisé à y accéder.
            </Alert>
            <div className="text-center mt-3 mb-3">
              <Link to="/espace-partenaire">
                <Button variant="primary">Retour à l'accueil</Button>
              </Link>
            </div>
          </Container>
        </Section>
        <FooterStandard />
      </>
    );
  }

  return (
    <>
      <NavbarStandard />
      <Section
        bg="light"
        className="d-flex align-items-end p-1 mt-7"
        id="actualites"
        style={{ backgroundImage: `url("${bgPartners}")` }}
      >
        <Row>
          <Col xs={12}>
            <h2>Espace partenaire</h2>
          </Col>
        </Row>
      </Section>

      <Section className="py-4 overflow-hidden light" position="center bottom">
        <Container>
          <Row className="mb-3 align-items-center">
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                as={Link}
                to="/espace-partenaire"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
            </Col>
            <Col>
              <h3 className="mb-0">{salon.name}</h3>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col xs={12}>
              <div className="d-flex flex-wrap">
                <Badge bg="light" text="dark" className="me-2 mb-2 border p-2">
                  <FontAwesomeIcon icon={faCalendar} className="me-1" />
                  Date: {Moment(salon.date).format('DD/MM/YYYY')}
                </Badge>
                <Badge bg="light" text="dark" className="me-2 mb-2 border p-2">
                  <FontAwesomeIcon icon={faClock} className="me-1" />
                  Horaires: {salon.start_time} - {salon.end_time}
                </Badge>
                <Badge bg="light" text="dark" className="me-2 mb-2 border p-2">
                  <FontAwesomeIcon icon={faBusinessTime} className="me-1" />
                  Durée des RDV: {salon.slot_duration} min
                </Badge>
                <Badge bg="light" text="dark" className="me-2 mb-2 border p-2">
                  <FontAwesomeIcon icon={faUserClock} className="me-1" />
                  Inscriptions jusqu'au:{' '}
                  {Moment(salon.registration_end_date).format('DD/MM/YYYY')}
                </Badge>
              </div>
            </Col>
          </Row>

          {isRegistrationOpen && (
            <Card className="mb-4 bg-light border-info">
              <Card.Body>
                <p className="mb-1">
                  <FontAwesomeIcon
                    icon={faHandshake}
                    className="me-2 text-info"
                  />
                  Pour chaque participant, cliquez sur les sociétés que vous
                  souhaitez rencontrer pour exprimer vos vœux.
                </p>
                <p className="mb-0">
                  <FontAwesomeIcon
                    icon={faBuilding}
                    className="me-2 text-info"
                  />
                  Les sociétés marquées comme "Favoris" sont celles avec
                  lesquelles vous travaillez régulièrement.
                </p>
              </Card.Body>
            </Card>
          )}

          <Card>
            <Card.Header className="bg-light">
              <Nav
                variant="tabs"
                activeKey={activeTab}
                onSelect={handleTabChange}
              >
                <Nav.Item>
                  <Nav.Link eventKey="summary">
                    <FontAwesomeIcon icon={faListAlt} className="me-1" />{' '}
                    Récapitulatif
                  </Nav.Link>
                </Nav.Item>
                {participants.map(p => (
                  <Nav.Item key={p.id}>
                    <Nav.Link eventKey={p.id.toString()}>
                      <FontAwesomeIcon icon={faUser} className="me-1" />{' '}
                      {p.firstname} {p.lastname}
                    </Nav.Link>
                  </Nav.Item>
                ))}
                {isRegistrationOpen && (
                  <Nav.Item>
                    <Nav.Link eventKey="add">
                      <FontAwesomeIcon icon={faPlus} className="me-1" /> Ajouter
                      un participant
                    </Nav.Link>
                  </Nav.Item>
                )}
              </Nav>
            </Card.Header>
            <Card.Body>
              {activeTab === 'summary' && (
                <>
                  {participants.length === 0 && !isRegistrationOpen && (
                    <p>
                      Aucun participant n'a été inscrit et les inscriptions sont
                      closes.
                    </p>
                  )}
                  {participants.length === 0 && isRegistrationOpen && (
                    <div className="text-center py-4">
                      <div className="mb-3">
                        <FontAwesomeIcon
                          icon={faUsers}
                          size="3x"
                          className="text-muted"
                        />
                      </div>
                      <p className="lead mb-3">
                        Aucun participant n'a encore été ajouté à ce salon.
                      </p>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() => handleTabChange('add')}
                      >
                        <FontAwesomeIcon icon={faUserPlus} className="me-2" />{' '}
                        Ajouter un participant
                      </Button>
                    </div>
                  )}
                  {participants.length > 0 && (
                    <Table responsive striped hover className="mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '30%' }}>Participant</th>
                          <th style={{ width: '60%' }}>Souhaite rencontrer</th>
                          <th style={{ width: '10%' }} className="text-center">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getWishSummary().map(item => (
                          <tr key={item.participant.id}>
                            <td>
                              <Button
                                variant="link"
                                className="p-0 text-decoration-none"
                                onClick={() =>
                                  setActiveTab(item.participant.id.toString())
                                }
                              >
                                <strong>{item.participant.lastname}</strong>{' '}
                                {item.participant.firstname}
                              </Button>
                            </td>
                            <td>
                              {item.wishedCompanies.length > 0 ? (
                                <div className="d-flex flex-wrap gap-1">
                                  {item.wishedCompanies
                                    .slice(0, 5)
                                    .map(company => (
                                      <Badge
                                        bg="light"
                                        text="dark"
                                        key={company.id}
                                        className="border"
                                      >
                                        {company.name}
                                      </Badge>
                                    ))}
                                  {item.wishedCompanies.length > 5 && (
                                    <Badge bg="secondary" pill>
                                      +{item.wishedCompanies.length - 5}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted fst-italic">
                                  Aucune société sélectionnée
                                </span>
                              )}
                            </td>
                            <td className="text-center">
                              <Badge bg="primary" pill>
                                {item.count}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </>
              )}

              {participants.map(
                participant =>
                  activeTab === participant.id.toString() && (
                    <div key={participant.id}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                          Vœux pour {participant.firstname}{' '}
                          {participant.lastname}
                        </h5>
                        {isRegistrationOpen && participant.user_id === null && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() =>
                              confirmDeleteParticipant(participant)
                            }
                          >
                            <FontAwesomeIcon icon={faTrash} className="me-1" />{' '}
                            Supprimer
                          </Button>
                        )}
                      </div>
                      <Row className="mb-3">
                        <Col md={8}>
                          <Form.Control
                            type="text"
                            placeholder="Rechercher une société..."
                            value={searchCompany}
                            onChange={e => setSearchCompany(e.target.value)}
                          />
                        </Col>
                        <Col
                          md={4}
                          className="d-flex justify-content-end align-items-center"
                        >
                          <Form.Check
                            type="switch"
                            id={`show-only-wished-switch-${participant.id}`}
                            label="Voir uniquement mes vœux"
                            checked={showOnlyWishedToggle}
                            onChange={() =>
                              setShowOnlyWishedToggle(!showOnlyWishedToggle)
                            }
                          />
                        </Col>
                      </Row>
                      {(() => {
                        const companiesToDisplayCurrentTab =
                          showOnlyWishedToggle
                            ? filteredCompaniesForDisplay.filter(c =>
                                isCompanyWishedByParticipant(
                                  participant.id,
                                  c.id
                                )
                              )
                            : filteredCompaniesForDisplay;

                        if (
                          companiesToDisplayCurrentTab.length === 0 &&
                          showOnlyWishedToggle
                        )
                          return (
                            <p>
                              Aucun vœu sélectionné. Décochez le filtre
                              ci-dessus pour voir toutes les sociétés.
                            </p>
                          );
                        if (
                          companiesToDisplayCurrentTab.length === 0 &&
                          !showOnlyWishedToggle
                        )
                          return <p>Aucune société trouvée.</p>;

                        companiesToDisplayCurrentTab.sort(
                          sortByPartnerCompanyAndName
                        );

                        return (
                          <Row xs={1} md={2} lg={3} className="g-3">
                            {companiesToDisplayCurrentTab.map(company => {
                              const isWished = isCompanyWishedByParticipant(
                                participant.id,
                                company.id
                              );
                              return (
                                <Col key={company.id}>
                                  <Card
                                    className={`company-card h-100 ${
                                      isWished ? 'selected-company' : ''
                                    } ${
                                      company.is_partner_company // La condition reste basée sur is_partner_company
                                        ? 'partner-company' // La classe CSS peut rester pour le style
                                        : ''
                                    }`}
                                    style={{
                                      cursor: isRegistrationOpen
                                        ? 'pointer'
                                        : 'default'
                                    }}
                                    onClick={() =>
                                      isRegistrationOpen &&
                                      toggleParticipantWish(
                                        participant.id,
                                        company.id,
                                        isWished
                                      )
                                    }
                                  >
                                    <Card.Body className="p-3 d-flex align-items-center justify-content-between">
                                      <span className="d-flex align-items-center">
                                        {isWished && (
                                          <FontAwesomeIcon
                                            icon={faHandshake}
                                            className="text-success me-2"
                                          />
                                        )}
                                        {!isWished && (
                                          <FontAwesomeIcon
                                            icon={faBuilding}
                                            className="text-muted me-2"
                                          />
                                        )}
                                        {company.name}
                                      </span>
                                      {company.is_partner_company && (
                                        <Badge bg="info" pill>
                                          Favoris {/* Texte du badge modifié */}
                                        </Badge>
                                      )}
                                    </Card.Body>
                                  </Card>
                                </Col>
                              );
                            })}
                          </Row>
                        );
                      })()}
                    </div>
                  )
              )}
            </Card.Body>
          </Card>
        </Container>
      </Section>

      <Modal
        show={showAddParticipantModal}
        onHide={() => setShowAddParticipantModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Ajouter un participant</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Prénom</Form.Label>
              <Form.Control
                type="text"
                name="firstname"
                value={participantForm.firstname}
                onChange={handleParticipantFormChange}
                placeholder="Prénom"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nom</Form.Label>
              <Form.Control
                type="text"
                name="lastname"
                value={participantForm.lastname}
                onChange={handleParticipantFormChange}
                placeholder="Nom"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="text"
                name="email"
                value={participantForm.email}
                onChange={handleParticipantFormChange}
                placeholder="Email"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Téléphone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={participantForm.phone}
                onChange={handleParticipantFormChange}
                placeholder="Téléphone"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddParticipantModal(false)}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={addParticipant}
            disabled={
              !participantForm.firstname ||
              !participantForm.lastname ||
              !participantForm.email ||
              !participantForm.phone
            }
          >
            Ajouter
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer{' '}
          <strong>
            {participantToDelete?.firstname} {participantToDelete?.lastname}
          </strong>{' '}
          et tous ses vœux ?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Annuler
          </Button>
          <Button variant="danger" onClick={deleteParticipant}>
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>
      <FooterStandard />
    </>
  );
};

export default SalonDetail;
